import React, { useState } from 'react';
import { Button, Popconfirm, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './InvoiceTables.css';
import { useNavigate } from 'react-router-dom';
import type { OrderSalesInvoice, OrderSalesInvoiceState } from '../../../types/orderSalesInvoice';
import { deleteSalesInvoiceDraft } from '../api/salesInvoiceImportApi';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { formatRecognitionMonth } from '../invoiceCoverageLabels';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/money';
import { useFeedback } from '../../../utils/feedback';
import InvoiceWithdrawModal from './InvoiceWithdrawModal';

const STATE_COLOR: Record<OrderSalesInvoiceState, string> = {
  DRAFT: 'gold', APPROVED: 'green', WITHDRAWN: 'default', OTHER: 'default',
};

const money = (value: number | null, currency: string | null) => (
  value == null ? '—' : `${formatMoney(value, 'pl')}${currency ? ` ${currency}` : ''}`
);

/**
 * „Faktury sprzedaży” of the ZK card (decision 2026-10-19 „Usuń fakturę na
 * ZK”): every sales invoice of the order with its state and amounts. „Usuń
 * fakturę” deletes a draft; an approved invoice is withdrawn (reason, month)
 * and stays in the list as „Wycofana”. Nothing approved is ever erased.
 */
export default function OrderSalesInvoicesTable({ invoices, onChanged }: {
  invoices: OrderSalesInvoice[];
  /** An invoice was deleted or withdrawn: reload the list and the order. */
  onChanged: () => void;
}) {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<OrderSalesInvoice | null>(null);

  const label = (invoice: OrderSalesInvoice) => invoice.number || t('invoices:workflow.coverage.unnumbered');

  const deleteDraft = async (invoice: OrderSalesInvoice) => {
    setDeleting(invoice.id);
    try {
      await deleteSalesInvoiceDraft(invoice.id);
      feedback.success(t('invoices:orderInvoices.draftDeleted', { number: label(invoice) }));
      onChanged();
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:orderInvoices.deleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  const action = (invoice: OrderSalesInvoice) => {
    // An icon with its name in the tooltip, so Netto/Brutto stay in view at 1024 px (staging v3.27.0).
    const button = (props: React.ComponentProps<typeof Button> = {}) => (
      <Button size="small" danger icon={<DeleteOutlined />} aria-label={t('invoices:orderInvoices.delete')}
        data-testid="order-sales-invoice-delete" {...props} />
    );
    const named = (node: React.ReactNode) => <Tooltip title={t('invoices:orderInvoices.delete')}>{node}</Tooltip>;
    if (invoice.canDelete) {
      return <Popconfirm
        title={t('invoices:orderInvoices.deleteDraftTitle', { number: label(invoice) })}
        description={t('invoices:orderInvoices.deleteDraftHint')}
        okText={t('invoices:orderInvoices.delete')}
        okButtonProps={{ danger: true }}
        cancelText={t('common:cancel')}
        onConfirm={() => deleteDraft(invoice)}
      >
        {named(button({ loading: deleting === invoice.id }))}
      </Popconfirm>;
    }
    if (invoice.canWithdraw) return named(button({ onClick: () => setWithdrawing(invoice) }));
    if (invoice.deleteBlockedReason === 'COMPLAINT') {
      return <Tooltip title={t('invoices:orderInvoices.blockedComplaint')}>{button({ disabled: true })}</Tooltip>;
    }
    return null;
  };

  const anyAction = invoices.some((invoice) => invoice.canDelete || invoice.canWithdraw || invoice.deleteBlockedReason);

  return <>
    <Table<OrderSalesInvoice>
      className="invoice-table"
      tableLayout="fixed"
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={invoices}
      // Fits the card down to 1024 px without a horizontal scroll (staging v3.27.0 hid Netto/Brutto
      // and „Usuń fakturę”): the invoice column takes the slack and carries its state under the
      // number (one line, „…”, full number in `title`); figures and the icon action keep their width.
      data-testid="order-sales-invoices"
      columns={[
        {
          title: t('invoices:orderInvoices.invoice'),
          render: (_, invoice) => <div>
            <Button type="link" className="invoice-table__link" title={label(invoice)}
              onClick={() => navigate(`/app/sales-invoices/${invoice.id}`)}>
              {label(invoice)}
            </Button>
            {(invoice.documentKind === 'ADVANCE' || invoice.documentKind === 'SETTLEMENT' || invoice.documentKind === 'PROFORMA') && <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {' · '}{t(`invoices:workflow.kind.${invoice.documentKind.toLowerCase()}`)}
            </Typography.Text>}
            <div>
              {/* A proforma is only saved, never approved (decision 2026-10-27): no „Szkic” look. */}
              {invoice.documentKind === 'PROFORMA' && invoice.state === 'DRAFT'
                ? <Tag color="green" className="invoice-table__state" data-testid="order-sales-invoice-state">{t('invoices:workflow.page.status.saved')}</Tag>
                : <Tag color={STATE_COLOR[invoice.state]} className="invoice-table__state" data-testid="order-sales-invoice-state">
                  {t(`invoices:orderInvoices.state.${invoice.state}`)}
                </Tag>}
            </div>
            {/* Hints on one line with „…” and the full text in `title` (DESIGN.md „One line”). */}
            {invoice.corrected && <Typography.Text type="secondary" className="invoice-table__one-line" style={{ fontSize: 12 }}
              title={t('invoices:orderInvoices.correctedBy', { number: invoice.correctionNumber || t('invoices:workflow.coverage.unnumbered') })}>
              {t('invoices:orderInvoices.correctedBy', { number: invoice.correctionNumber || t('invoices:workflow.coverage.unnumbered') })}
            </Typography.Text>}
            {invoice.withdrawal && <Typography.Text type="secondary" className="invoice-table__one-line" style={{ fontSize: 12 }}
              title={t('invoices:orderInvoices.withdrawnHint', {
                month: formatRecognitionMonth(invoice.withdrawal.recognitionMonth) || invoice.withdrawal.recognitionMonth,
                reason: invoice.withdrawal.reason,
              })}>
              {t('invoices:orderInvoices.withdrawnHint', {
                month: formatRecognitionMonth(invoice.withdrawal.recognitionMonth) || invoice.withdrawal.recognitionMonth,
                reason: invoice.withdrawal.reason,
              })}
            </Typography.Text>}
          </div>,
        },
        { title: t('invoices:orderInvoices.issueDate'), width: 108, render: (_, invoice) => (invoice.issueDate ? formatDateEuropean(invoice.issueDate) : '—') },
        { title: t('invoices:orderInvoices.month'), width: 76, render: (_, invoice) => formatRecognitionMonth(invoice.recognitionMonth) || '—' },
        {
          title: t('invoices:orderInvoices.net'), align: 'right', width: 120,
          render: (_, invoice) => <span className="invoice-table__amount">{money(invoice.netAmount, invoice.currency)}</span>,
        },
        {
          title: t('invoices:orderInvoices.gross'), align: 'right', width: 120,
          render: (_, invoice) => <span className="invoice-table__amount">{money(invoice.grossAmount, invoice.currency)}</span>,
        },
        ...(anyAction ? [{ key: 'action', align: 'right' as const, width: 48, render: (_: unknown, invoice: OrderSalesInvoice) => action(invoice) }] : []),
      ]}
    />
    {withdrawing && <InvoiceWithdrawModal open direction="SALES" invoiceId={withdrawing.id} number={withdrawing.number}
      suggestedMonth={withdrawing.withdrawalMonthSuggestion}
      onClose={() => setWithdrawing(null)} onWithdrawn={onChanged} />}
  </>;
}
